import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Route } from '@app/core/enums/route.enum';
import { RegistrationDataService } from '@app/core/services/registration-data.service';
import { isSessionExpiredError } from '@app/core/services/authentication.service';
import { AccountState } from '@app/core/types/account-state.types';
import { ShareDataSandbox } from '@app/share-data/sandboxes/share-data.sandbox';
import { ConsentSandbox } from '@app/consent/sandboxes/consent.sandbox';
import { RegistrationService } from '@app/registration/services/registration.service';
import { RegistrationSandbox } from '@app/registration/sandboxes/registration.sandbox';

/**
 * Decides whether an authenticated user may enter the app or still has to be
 * provisioned. One call to `/identity/me/account-state` returns the verdict, and
 * this guard translates it into a flow.
 *
 * The verdict is the backend's; nothing here infers state from local storage.
 * The library derived re-consent from an `account_was_linked` breadcrumb, which
 * is why accepting terms could end up recorded nowhere (finding F-01).
 *
 * It returns a destination and nothing else. An earlier version also set a mode
 * on a sandbox before navigating, which made the route meaningless without that
 * side effect having run (finding F-22).
 */
export const accountStateGuard = (): Observable<boolean | UrlTree> => {
  const registrationService = inject(RegistrationService);
  const registrationDataService = inject(RegistrationDataService);
  const shareDataSandbox = inject(ShareDataSandbox);
  const registrationSandbox = inject(RegistrationSandbox);
  const consentSandbox = inject(ConsentSandbox);
  const router = inject(Router);

  const toRegistration = () => router.createUrlTree([Route.Registration]);
  const toShareData = () => router.createUrlTree([Route.ShareData]);
  const toConsent = () => router.createUrlTree([Route.Consent]);
  const toLinkEmail = () => router.createUrlTree([Route.Registration, Route.LinkEmail]);

  return registrationService.getAccountState().pipe(
    map(({ userState, profileState }: AccountState) => {
      // 1. No identity yet: either register one, or prove ownership of the
      //    address an existing account already uses. Both start registration,
      //    so this is the one place that logs it, whichever flow follows.
      if (!userState.exists) {
        if (userState.needsToRegister) {
          const fields = userState.userRegisterFields;
          if (fields) {
            // Carry whatever the backend already knows into the wizard, so a
            // social sign-up that supplied a name does not ask for it again.
            // An invitation prefill takes priority, so these only fill the
            // fields it left empty.
            const current = registrationDataService.registrationData();
            registrationDataService.setRegistrationData({
              firstName: current.firstName || fields.firstName,
              lastName: current.lastName || fields.lastName,
              email: current.email || fields.email,
              phoneNumber: current.phoneNumber || fields.phoneNumber,
            });
          }
          registrationSandbox.logRegistrationStart();
          return toRegistration();
        }

        if (userState.needsToLinkEmail && userState.userLinkEmailFields?.email) {
          // The address is the only thing that route needs, and its own guard
          // refuses to mount the page without it.
          registrationDataService.setRegistrationData({
            email: userState.userLinkEmailFields.email,
          });
          registrationSandbox.logRegistrationStart();
          return toLinkEmail();
        }

        // Neither flag set: the backend cannot tell us what to do.
        return router.createUrlTree([Route.RegistrationError]);
      }

      // 2. Identity exists but this brand has no profile yet. Other brands hold
      //    data it can reuse, so the share-data step creates the profile; it
      //    also collects consent to the terms and privacy policy, which is why
      //    it comes before the consent check below.
      if (!profileState.exists) {
        if (profileState.needsSharing) {
          shareDataSandbox.setBrands(profileState.sharingBrands ?? []);
          shareDataSandbox.logShareDataStart();
          return toShareData();
        }

        // The backend guarantees a missing profile always comes with
        // needsSharing (or the flags in step 1). If it doesn't, stop here
        // instead of letting a user without a profile into the app.
        return router.createUrlTree([Route.RegistrationError]);
      }

      // 3. Provisioned, but a newer terms or privacy policy version is live
      //    that the user hasn't accepted yet.
      if (profileState.consentNeeded) {
        consentSandbox.logConsentUpdateStart();
        return toConsent();
      }

      return true;
    }),
    catchError((error: unknown) =>
      // An expired session fails this call before it reaches the backend, and
      // AuthenticationService is already logging the user out and will navigate
      // to login. Cancel instead of claiming the registration failed.
      isSessionExpiredError(error) ? of(false) : of(router.createUrlTree([Route.RegistrationError]))
    )
  );
};
